import { BLApiError } from "./errors";
import { AnyObject, RequestParams } from "./types";
import { isJsonResponse, parsePath } from "./utils";

export class BLApi extends EventTarget {
	protected readonly corsMode: boolean;

	constructor(corsMode = false, public readonly apiHost: string) {
		super();
		this.corsMode = corsMode;
	}

	private sendRequestWithBody = async <T = any>(
		url: string,
		method: 'POST' | 'PUT' | 'PATCH' | 'DELETE',
		body?: AnyObject,
		options?: RequestInit,
		token?: string,
		host?: string
	): Promise<T> => {
		url = parsePath(url, host ?? this.apiHost);
		let requestInit: RequestInit = {};
		if (this.corsMode) {
			requestInit = {
				...options,
				method: method,
				headers: {
					'bl-auth-method': 'bearer',
					'authorization': `Bearer ${token}`,
					...options?.headers,
				},
				body: JSON.stringify(body)
			};
		} else {
			requestInit = {
				...options,
				method: method,
				credentials: 'include',
				headers: {
					...options?.headers,
				},
				body: JSON.stringify(body)
			};
		}

		const res = await fetch(url, requestInit);
		return this.parseResponse<T>(url, res);
	};

	private parseResponse = async <T = any>(url: string, res: Response): Promise<T> => {
		if (isJsonResponse(res)) {
			if (res.ok) {
				return res.json();
			} else {
				const err = await res.json();
				throw new BLApiError(res.status, `Error GET ${url}: ${res.statusText}`, err);
			}
		} else {
			if (res.ok) {
				return res.text() as unknown as T;
			} else {
				throw new BLApiError(res.status, `Error GET ${url}: ${res.statusText}`);
			}
		}
	};

	public get = async <T = any>({ url, options, token, host }: RequestParams): Promise<T> => {
		url = parsePath(url, host ?? this.apiHost);
		let requestInit: RequestInit = {};
		if (this.corsMode) {
			requestInit = {
				...options,
				method: 'GET',
				headers: {
					'bl-auth-method': 'bearer',
					'authorization': `Bearer ${token}`,
					...options?.headers,
				}
			};
		} else {
			requestInit = {
				...options,
				method: 'GET',
				credentials: 'include',
				headers: {
					...options?.headers,
				},
			};
		}

		const res = await fetch(url, requestInit);
		return this.parseResponse<T>(url, res);
	};

	public put = async <T = any>({
		url,
		body,
		options,
		token,
		host
	}: RequestParams): Promise<T> => {
		return this.sendRequestWithBody(url, 'PUT', body, options, token, host);
	};

	public post = async <T = any>({
		url,
		body,
		options,
		token,
		host
	}: RequestParams): Promise<T> => {
		return this.sendRequestWithBody(url, 'POST', body, options, token, host);
	};

	public patch = async < T = any > ({
		url,
		body,
		options,
		token,
		host
	}: RequestParams): Promise<T> => {
		return this.sendRequestWithBody(url, 'PATCH', body, options, token, host);
	};

	public delete = async <T = any>({
		url,
		body,
		options,
		token,
		host
	}: RequestParams): Promise<T> => {
		return this.sendRequestWithBody(url, 'DELETE', body, options, token, host);
	};
}